import { useState, useCallback, useEffect } from 'react';
import type { Paper } from '../types';
import { reconstructAbstract } from '../utils/abstract';

export const usePapers = (activeTopic: string, view: string, dateFrom: string, dateTo: string) => {
    const [papers, setPapers] = useState<Paper[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Helper for aggressive deduplication
    const deduplicateBatch = (newPapers: Paper[], existingPapers: Paper[] = []) => {
        const seenIds = new Set(existingPapers.map(p => p.id));
        const seenDois = new Set(existingPapers.map(p => p.doi).filter(Boolean) as string[]);
        const seenTitles = new Set(existingPapers.map(p => (p.title || '').toLowerCase().trim()).filter(Boolean));

        return newPapers.filter(paper => {
            const normalizedTitle = (paper.title || '').toLowerCase().trim();

            // Check ID (Exact match)
            if (seenIds.has(paper.id)) return false;

            // Check DOI (Different version of same registered paper)
            if (paper.doi && seenDois.has(paper.doi)) return false;

            // Check Title (Visual duplicate) - only if title exists
            if (normalizedTitle && seenTitles.has(normalizedTitle)) return false;

            // Add to tracked sets
            seenIds.add(paper.id);
            if (paper.doi) seenDois.add(paper.doi);
            if (normalizedTitle) seenTitles.add(normalizedTitle);

            return true;
        });
    };

    const fetchPapers = useCallback(async (query: string, pageNum = 1, shouldAppend = false) => {
        setLoading(true);

        try {
            let url = `https://api.openalex.org/works?search=${encodeURIComponent(query)}&sort=publication_date:desc&per_page=24&page=${pageNum}&mailto=eolas_demo@example.com`;

            // Add date range filters
            if (dateFrom || dateTo) {
                const filters = [];
                if (dateFrom) filters.push(`from_publication_date:${dateFrom}`);
                if (dateTo) filters.push(`to_publication_date:${dateTo}`);
                url += `&filter=${filters.join(',')}`;
            }

            const response = await fetch(url);
            const data = await response.json();

            const processed: Paper[] = data.results.map((item: any) => ({
                id: item.id,
                title: item.title || 'Untitled Paper',
                publication_year: item.publication_year,
                publication_date: item.publication_date,
                authorships: item.authorships,
                abstract: reconstructAbstract(item.abstract_inverted_index),
                doi: item.doi,
                type: item.type,
                cited_by_count: item.cited_by_count,
                institutions: item.authorships
                    .flatMap((a: any) => a.institutions.map((i: any) => i.display_name))
                    .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i),
                venue: item.primary_location?.source?.display_name || item.host_venue?.display_name || 'Scientific Repository',
                primary_location: item.primary_location,
                open_access: item.open_access
            }));

            if (shouldAppend) {
                setPapers(prev => {
                    const uniqueNew = deduplicateBatch(processed, prev);
                    return [...prev, ...uniqueNew];
                });
            } else {
                const uniqueProcessed = deduplicateBatch(processed, []);
                setPapers(uniqueProcessed);
            }

            setHasMore(processed.length === 24);

        } catch (error) {
            console.error("Failed to fetch papers:", error);
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => {
        if (view === 'feed') {
            setPage(1);
            fetchPapers(activeTopic, 1, false);
        }
    }, [activeTopic, view, dateFrom, dateTo, fetchPapers]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchPapers(activeTopic, nextPage, true);
    };

    return {
        papers,
        loading,
        hasMore,
        handleLoadMore,
        fetchPapers,
        setPapers
    };
};
